import * as THREE from 'three/webgpu'
import type { Viewer } from '../../viewer/Viewer'
import { ImportedModelObjectMetadataService } from '../../imported-model/metadata'
import { ImportedModelMeshIdentityResolver } from '../../imported-model/selection'
import { ImportedModelInstancedMeshUnit } from '../../imported-model/instancing/mesh-unit'
import { ImportedModelInstancingEligibilityPolicy } from '../../imported-model/instancing/prototype'
import { ImportedModelSourceMeshVisibilityController } from '../../imported-model/instancing/suppression'
import { ImportedModelTopologySourceLookupAnchorRegistry } from '../../imported-model/instancing/topology'
import { isImportedModelTopologyHost } from '../../imported-model/topology/imported-model-topology-runtime'
import type { LinearComponentPatternCommittedRecord } from '../LinearComponentPatternTypes'

/**
 * 单个线性阵列特征组拥有的独立 InstancedMesh 投影。
 *
 * 设计说明：
 * 1. 装配体加载阶段的全局 Unit 是稳定基线，阵列提交不得向其注入 occurrence 或触发重建；
 * 2. 本对象只读取当前阵列组内的业务复制体，按来源组件和 Mesh 槽位创建新的 Unit；
 * 3. 业务 Mesh 继续承担 metadata、拓扑和历史身份，但发布后从物理树可逆脱离，由本组 InstancedMesh 接管；
 * 4. Unit 仍注册到 Viewer 级命中表，使 instanceId 能回映射到精确阵列 occurrence source Mesh；
 * 5. 销毁只释放当前阵列组的 GPU 对象和注册关系，不接触装配体加载阶段已有 Unit。
 */
export class LinearComponentPatternInstancedGroupProjection {
  /** 当前阵列组创建的全部实例 Unit。 */
  private readonly units: ImportedModelInstancedMeshUnit[] = []

  /** 当前阵列组 source Mesh 的业务父链与投影抑制控制器。 */
  private readonly sourceMeshVisibilityController = new ImportedModelSourceMeshVisibilityController()

  /** source Mesh 脱离物理父链后的拓扑仓库反查锚点注册表。 */
  private readonly topologySourceLookupAnchorRegistry
    = new ImportedModelTopologySourceLookupAnchorRegistry()

  /** 组件变换对象到受影响实例槽绑定的反向索引。 */
  private readonly bindingsByTransformObject = new Map<
    THREE.Object3D,
    Array<readonly [ImportedModelInstancedMeshUnit, THREE.Mesh]>
  >()

  /** 增量矩阵更新使用的阵列组世界逆矩阵。 */
  private readonly inverseGroupWorldMatrix = new THREE.Matrix4()

  /** 当前投影是否已经释放。 */
  private disposed = false

  /** source Mesh 脱离控制器是否已注册到 Viewer 逻辑子树门面。 */
  private sourceMeshVisibilityControllerRegistered = false

  /** 阵列专用实例资格判定器。 */
  private readonly eligibilityPolicy = new ImportedModelInstancingEligibilityPolicy()

  /**
   * 创建并发布一个阵列组级实例投影。
   *
   * @param viewer 当前 Viewer。
   * @param group 当前阵列特征组。
   * @param record 当前阵列正式记录。
   * @param generation 阵列投影结构代次。
   */
  public constructor(
    private readonly viewer: Viewer,
    private readonly group: THREE.Group,
    private readonly record: LinearComponentPatternCommittedRecord,
    generation: number,
  ) {
    try {
      this.publish(generation)
    } catch (error) {
      this.dispose()
      throw error
    }
  }

  /**
   * 判断投影是否仍绑定指定阵列组对象。
   *
   * @param group 待比较阵列组。
   * @returns 是否为同一个业务组对象。
   */
  public isBoundTo(group: THREE.Group): boolean {
    return this.group === group
  }

  /**
   * 增量更新当前阵列组中受组件变换影响的实例槽。
   *
   * @param changedObjects 本轮发生变换的业务对象。
   * @returns 实际更新的实例槽数量。
   */
  public updateTransforms(changedObjects: readonly THREE.Object3D[]): number {
    if (this.disposed || changedObjects.length === 0) {
      return 0
    }

    const bindings = new Set<readonly [ImportedModelInstancedMeshUnit, THREE.Mesh]>()
    changedObjects.forEach((object) => {
      this.bindingsByTransformObject.get(object)?.forEach(binding => bindings.add(binding))
    })
    if (bindings.size === 0) {
      return 0
    }

    this.group.updateWorldMatrix(true, true)
    this.inverseGroupWorldMatrix.copy(this.group.matrixWorld).invert()
    const changedUnits = new Set<ImportedModelInstancedMeshUnit>()
    let changedSlotCount = 0
    bindings.forEach(([unit, sourceMesh]) => {
      if (!unit.syncSourceMeshTransform(sourceMesh, this.inverseGroupWorldMatrix)) {
        return
      }
      changedUnits.add(unit)
      changedSlotCount += 1
    })
    changedUnits.forEach(unit => unit.commitMatrixChanges())
    return changedSlotCount
  }

  /**
   * 发布阵列组内部的 InstancedMesh。
   *
   * @param generation 当前阵列投影结构代次。
   */
  private publish(generation: number): void {
    this.group.updateWorldMatrix(true, true)
    const slots = this.createRenderSlots()
    slots.forEach((slot, slotIndex) => {
      const eligibility = this.eligibilityPolicy.evaluate(slot, 1)
      if (!eligibility.eligible) {
        return
      }

      const unit = new ImportedModelInstancedMeshUnit(
        slot,
        this.group,
        generation,
        (sourceMesh, targetMatrix) => (
          this.sourceMeshVisibilityController.copyEffectiveWorldMatrix(sourceMesh, targetMatrix)
        ),
        sourceMesh => (
          this.sourceMeshVisibilityController.resolveOriginalParent(sourceMesh) ?? sourceMesh.parent
        ),
      )
      unit.object.name = `线性阵列实例组-${this.record.id}-${slotIndex + 1}`
      let registered = false
      try {
        this.viewer.registerImportedModelExternalInstancedMeshUnit(unit)
        registered = true
        this.group.add(unit.object)
      } catch (error) {
        // 注册与场景挂载必须具备事务语义，发布失败时不能在统一命中表中遗留悬空 Unit。
        if (registered) {
          this.viewer.unregisterImportedModelExternalInstancedMeshUnit(unit)
        }
        unit.dispose()
        throw error
      }
      this.units.push(unit)

      slot.members.forEach((member) => {
        const binding = [unit, member.sourceMesh] as const
        let current: THREE.Object3D | null = member.sourceMesh
        while (current) {
          const objectBindings = this.bindingsByTransformObject.get(current) ?? []
          objectBindings.push(binding)
          this.bindingsByTransformObject.set(current, objectBindings)
          if (current === this.group) {
            break
          }
          current = current.parent
        }
      })
    })

    if (this.units.length === 0) {
      return
    }

    /** source Mesh 脱离父链前建立拓扑反查和 host 变换投影，保证二阶段拾取链不中断。 */
    this.topologySourceLookupAnchorRegistry.registerUnits(this.units)
    const topologyHostTransformProjectionPort
      = this.viewer.getImportedModelTopologyHostTransformProjectionPort()
    this.topologySourceLookupAnchorRegistry.listRepositories().forEach((repository) => {
      repository.setTopologyHostTransformProjectionPort(topologyHostTransformProjectionPort)
    })

    /** Viewer 逻辑子树门面先接管控制器，再从阵列组物理树移除普通 imported Mesh。 */
    this.viewer.registerImportedModelExternalSourceMeshVisibilityController(
      this.sourceMeshVisibilityController,
    )
    this.sourceMeshVisibilityControllerRegistered = true
    this.units.forEach((unit) => {
      const ownerId = unit.object.uuid
      unit.listSourceMeshes().forEach((sourceMesh) => {
        this.sourceMeshVisibilityController.suppress(sourceMesh, ownerId)
      })
    })
  }

  /**
   * 从当前阵列记录构建组内渲染槽。
   *
   * 同一来源组件的各复制体由克隆链保证 Mesh 遍历顺序一致，因此使用稳定 sourceObjectId
   * 与 Mesh 顺序组成槽身份；Geometry、Material 和渲染状态仍参与键，避免错误合批。
   *
   * @returns 当前阵列组可参与实例化的渲染槽。
   */
  private createRenderSlots(): PuppyCAD.ImportedModelInstancingRenderSlot[] {
    const membersBySlotKey = new Map<string, PuppyCAD.ImportedModelInstancingSlotMember[]>()
    const seedBySlotKey = new Map<string, THREE.Mesh>()

    this.record.partRecords.forEach((partRecord) => {
      const occurrenceRoot = this.viewer.findSceneObjectById(partRecord.objectId)
      if (!occurrenceRoot) {
        return
      }
      this.collectSourceMeshes(occurrenceRoot).forEach((sourceMesh, meshIndex) => {
        const materialKey = (Array.isArray(sourceMesh.material)
          ? sourceMesh.material
          : [sourceMesh.material])
          .map(material => material.uuid)
          .join(',')
        const slotKey = [
          partRecord.sourceObjectId,
          meshIndex,
          sourceMesh.geometry.uuid,
          materialKey,
          sourceMesh.renderOrder,
          sourceMesh.layers.mask,
          sourceMesh.castShadow ? 1 : 0,
          sourceMesh.receiveShadow ? 1 : 0,
        ].join('|')
        const members = membersBySlotKey.get(slotKey) ?? []
        members.push(Object.freeze({
          occurrenceId: ImportedModelObjectMetadataService
            .resolveAssemblyNodeId(occurrenceRoot)
            ?.trim() || occurrenceRoot.uuid,
          occurrenceRoot,
          sourceMesh,
        }))
        membersBySlotKey.set(slotKey, members)
        seedBySlotKey.set(slotKey, seedBySlotKey.get(slotKey) ?? sourceMesh)
      })
    })

    return [...membersBySlotKey.entries()].map(([slotKey, members]) => {
      const seed = seedBySlotKey.get(slotKey)
      if (!seed) {
        throw new Error(`线性阵列实例槽缺少种子 Mesh：${slotKey}`)
      }
      return Object.freeze({
        definitionKey: `linear-component-pattern:${this.record.id}:${slotKey}`,
        slotKey,
        unitKey: `linear-component-pattern:${this.record.id}:${slotKey}`,
        geometry: seed.geometry,
        material: seed.material,
        members: Object.freeze([...members]),
      })
    })
  }

  /**
   * 收集一个阵列 occurrence 下可参与实例化的业务 Mesh。
   *
   * @param rootObject 当前阵列 occurrence 根。
   * @returns 保持业务树遍历顺序的 source Mesh。
   */
  private collectSourceMeshes(rootObject: THREE.Object3D): THREE.Mesh[] {
    const sourceMeshes: THREE.Mesh[] = []
    rootObject.traverse((object) => {
      if (
        !(object instanceof THREE.Mesh)
        || object instanceof THREE.InstancedMesh
        || isImportedModelTopologyHost(object)
        || !ImportedModelMeshIdentityResolver.isImportedVisibleMesh(object)
      ) {
        return
      }
      sourceMeshes.push(object)
    })
    return sourceMeshes
  }

  /** 幂等释放当前阵列组拥有的实例投影。 */
  public dispose(): void {
    if (this.disposed) {
      return
    }
    this.disposed = true
    /** Repository 必须在 Unit 仍可由 Registry 解析时解除 pick host 变换投影。 */
    this.topologySourceLookupAnchorRegistry.listRepositories().forEach((repository) => {
      repository.setTopologyHostTransformProjectionPort(null)
    })
    this.topologySourceLookupAnchorRegistry.clear()
    this.bindingsByTransformObject.clear()
    for (let index = this.units.length - 1; index >= 0; index -= 1) {
      const unit = this.units[index]
      if (!unit) {
        continue
      }
      this.sourceMeshVisibilityController.releaseOwner(unit.object.uuid)
      this.viewer.unregisterImportedModelExternalInstancedMeshUnit(unit)
      unit.dispose()
    }
    this.units.length = 0
    this.sourceMeshVisibilityController.dispose()
    if (this.sourceMeshVisibilityControllerRegistered) {
      this.viewer.unregisterImportedModelExternalSourceMeshVisibilityController(
        this.sourceMeshVisibilityController,
      )
      this.sourceMeshVisibilityControllerRegistered = false
    }
  }
}
